import { useState, useEffect, useRef } from "react";

const CATEGORIES = [
  { id: "all", label: "All Tools", icon: "⚡" },
  { id: "writing", label: "Writing & Content", icon: "✍️" },
  { id: "image", label: "Image Generation", icon: "🎨" },
  { id: "video", label: "Video & Animation", icon: "🎬" },
  { id: "audio", label: "Audio & Music", icon: "🎵" },
  { id: "code", label: "Code & Dev", icon: "💻" },
  { id: "productivity", label: "Productivity", icon: "🚀" },
  { id: "research", label: "Research & Analysis", icon: "🔬" },
  { id: "chatbot", label: "Chatbots & Assistants", icon: "🤖" },
  { id: "design", label: "Design & UI", icon: "🖌️" },
  { id: "data", label: "Data & Analytics", icon: "📊" },
  { id: "education", label: "Education & Learning", icon: "📚" },
  { id: "marketing", label: "Marketing & SEO", icon: "📈" },
  { id: "social", label: "Social Media", icon: "📱" },
  { id: "business", label: "Business & Finance", icon: "💼" },
  { id: "health", label: "Health & Wellness", icon: "🏥" },
  { id: "gaming", label: "Gaming", icon: "🎮" },
  { id: "translation", label: "Translation & Language", icon: "🌍" },
  { id: "automation", label: "Automation & Workflow", icon: "⚙️" },
  { id: "search", label: "Search & Discovery", icon: "🔍" },
  { id: "security", label: "Security & Privacy", icon: "🛡️" },
  { id: "other", label: "Other", icon: "✨" },
];

const SORT_OPTIONS = [
  { id: "newest", label: "Newest" },
  { id: "upvotes", label: "Most Upvoted" },
  { id: "name", label: "A–Z" },
];

const SEED_TOOLS = [
  {
    id: "seed-1",
    name: "ChatGPT",
    url: "https://chat.openai.com",
    description: "OpenAI's flagship conversational AI assistant for writing, coding, analysis, and more.",
    category: "chatbot",
    emoji: "🤖",
    upvotes: 482,
    upvotedBy: [],
    submittedBy: "AIHunter",
    createdAt: Date.now() - 7 * 86400000,
  },
  {
    id: "seed-2",
    name: "Midjourney",
    url: "https://midjourney.com",
    description: "AI art generator that creates stunning, photorealistic images from text prompts via Discord.",
    category: "image",
    emoji: "🎨",
    upvotes: 371,
    upvotedBy: [],
    submittedBy: "PixelMaker",
    createdAt: Date.now() - 5 * 86400000,
  },
  {
    id: "seed-3",
    name: "GitHub Copilot",
    url: "https://github.com/features/copilot",
    description: "AI-powered code completion that suggests whole lines and functions in your editor.",
    category: "code",
    emoji: "💻",
    upvotes: 295,
    upvotedBy: [],
    submittedBy: "DevBot",
    createdAt: Date.now() - 3 * 86400000,
  },
  {
    id: "seed-4",
    name: "Runway ML",
    url: "https://runwayml.com",
    description: "AI-powered creative tools for video editing, generation, and visual effects.",
    category: "video",
    emoji: "🎬",
    upvotes: 187,
    upvotedBy: [],
    submittedBy: "VideoNerd",
    createdAt: Date.now() - 2 * 86400000,
  },
  {
    id: "seed-5",
    name: "Notion AI",
    url: "https://notion.so/product/ai",
    description: "AI writing assistant built right into Notion for summarizing, drafting, and organizing notes.",
    category: "productivity",
    emoji: "📝",
    upvotes: 142,
    upvotedBy: [],
    submittedBy: "NotesTaker",
    createdAt: Date.now() - 1 * 86400000,
  },
];

const STORAGE_KEY = "ai_tools_hub_data";

function timeAgo(ts) {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function getDomain(url) {
  try {
    return new URL(url.startsWith("http") ? url : "https://" + url).hostname.replace("www.", "");
  } catch {
    return url;
  }
}

function getFaviconUrl(url) {
  try {
    const domain = new URL(url.startsWith("http") ? url : "https://" + url).origin;
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch {
    return null;
  }
}

export default function AIToolsHub() {
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [votedIds, setVotedIds] = useState(new Set());
  const [notification, setNotification] = useState(null);
  const [form, setForm] = useState({
    name: "", url: "", description: "", category: "other", emoji: "✨", submittedBy: ""
  });
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const result = { value: localStorage.getItem(STORAGE_KEY) };
      if (result && result.value) {
        const data = JSON.parse(result.value);
        setTools(data.tools || SEED_TOOLS);
        setVotedIds(new Set(data.votedIds || []));
      } else {
        setTools(SEED_TOOLS);
      }
    } catch {
      setTools(SEED_TOOLS);
    }
    setLoading(false);
  }

  async function saveData(newTools, newVotedIds) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        tools: newTools,
        votedIds: [...newVotedIds]
      }));
    } catch (e) {
      console.error("Save failed", e);
    }
  }

  function notify(msg, type = "success") {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 2800);
  }

  function handleUpvote(id) {
    if (votedIds.has(id)) {
      notify("Already upvoted!", "warn");
      return;
    }
    const updated = tools.map(t => t.id === id ? { ...t, upvotes: t.upvotes + 1 } : t);
    const newVoted = new Set([...votedIds, id]);
    setTools(updated);
    setVotedIds(newVoted);
    saveData(updated, newVoted);
    notify("Upvote added! 🚀");
  }

  async function handleSubmit() {
    setFormError("");
    if (!form.name.trim()) return setFormError("Tool name is required.");
    if (!form.url.trim()) return setFormError("URL is required.");
    if (!form.description.trim()) return setFormError("Description is required.");
    if (!form.submittedBy.trim()) return setFormError("Your name/handle is required.");
    let url = form.url.trim();
    if (!url.startsWith("http")) url = "https://" + url;
    try { new URL(url); } catch { return setFormError("Please enter a valid URL."); }

    setSubmitting(true);
    const newTool = {
      id: "tool-" + Date.now(),
      name: form.name.trim(),
      url,
      description: form.description.trim(),
      category: form.category,
      emoji: form.emoji || "✨",
      upvotes: 0,
      upvotedBy: [],
      submittedBy: form.submittedBy.trim(),
      createdAt: Date.now(),
    };
    const updated = [newTool, ...tools];
    await saveData(updated, votedIds);
    setTools(updated);
    setSubmitting(false);
    setShowModal(false);
    setForm({ name: "", url: "", description: "", category: "other", emoji: "✨", submittedBy: "" });
    setActiveCategory("all");
    setSortBy("newest");
    notify("Tool submitted! 🎉");
  }

  const filtered = tools
    .filter(t => activeCategory === "all" || t.category === activeCategory)
    .filter(t => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === "upvotes") return b.upvotes - a.upvotes;
      if (sortBy === "name") return a.name.localeCompare(b.name);
      return b.createdAt - a.createdAt;
    });

  const catCount = (catId) => catId === "all" ? tools.length : tools.filter(t => t.category === catId).length;

  const activeCat = CATEGORIES.find(c => c.id === activeCategory);

  return (
    <div style={{
      fontFamily: "'Syne', 'DM Sans', sans-serif",
      background: "#060810",
      minHeight: "100vh",
      color: "#e8eaf0",
      display: "flex",
      flexDirection: "column",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0d1020; }
        ::-webkit-scrollbar-thumb { background: #2a3050; border-radius: 4px; }
        .sidebar-link:hover { background: rgba(99,179,237,0.08) !important; color: #63b3ed !important; }
        .sidebar-link.active { background: rgba(99,179,237,0.12) !important; color: #63b3ed !important; }
        .tool-card { transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s; }
        .tool-card:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(99,179,237,0.10); border-color: #2a3a5a !important; }
        .upvote-btn { transition: all 0.15s; }
        .upvote-btn:hover:not(:disabled) { background: rgba(99,179,237,0.18) !important; border-color: #63b3ed !important; transform: scale(1.05); }
        .upvote-btn.voted { background: rgba(99,179,237,0.15) !important; border-color: #63b3ed !important; color: #63b3ed !important; }
        .modal-bg { animation: fadeIn 0.18s ease; }
        .modal-box { animation: slideUp 0.22s cubic-bezier(.22,1,.36,1); }
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { opacity:0;transform:translateY(24px) } to { opacity:1;transform:none } }
        .notif { animation: notifIn 0.25s cubic-bezier(.22,1,.36,1); }
        @keyframes notifIn { from { opacity:0;transform:translateY(10px) scale(0.96) } to { opacity:1;transform:none } }
        .tag-badge { transition: background 0.15s; }
        input, textarea, select { color-scheme: dark; }
        .submit-btn:hover { background: #4a9fd4 !important; }
        .add-btn:hover { opacity: 0.88; }
        .toggle-sidebar:hover { background: rgba(255,255,255,0.07) !important; }
        .sort-btn.active { background: rgba(99,179,237,0.18) !important; color: #63b3ed !important; }
        .sort-btn:hover { background: rgba(255,255,255,0.05) !important; }
        .tool-link:hover { color: #63b3ed !important; }
        .emoji-opt { cursor:pointer; border-radius:6px; padding:4px; transition:background 0.1s; }
        .emoji-opt:hover, .emoji-opt.sel { background: rgba(99,179,237,0.2); }
        .cat-pill:hover { opacity:0.8; }
      `}</style>

      {/* TOPBAR */}
      <header style={{
        background: "rgba(6,8,16,0.95)",
        borderBottom: "1px solid #151b30",
        padding: "0 24px",
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 100,
        backdropFilter: "blur(12px)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <button className="toggle-sidebar" onClick={() => setSidebarOpen(v => !v)} style={{
            background: "transparent", border: "1px solid #1e2540", borderRadius: 8, padding: "6px 10px",
            color: "#8892aa", cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center",
          }} title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}>
            {sidebarOpen ? "◀" : "▶"}
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 9, background: "linear-gradient(135deg, #1d4ed8, #0ea5e9)",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800,
            }}>⚡</div>
            <span style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 18, letterSpacing: "-0.5px" }}>
              AI<span style={{ color: "#0ea5e9" }}>Hunt</span>
            </span>
          </div>
        </div>

        <div style={{
          flex: 1, maxWidth: 400, margin: "0 24px",
          background: "#0d1020", border: "1px solid #1e2540", borderRadius: 10,
          display: "flex", alignItems: "center", padding: "0 14px", gap: 8,
        }}>
          <span style={{ color: "#4a5568", fontSize: 15 }}>🔍</span>
          <input
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search AI tools..."
            style={{
              background: "transparent", border: "none", outline: "none", flex: 1,
              color: "#e8eaf0", fontSize: 14, fontFamily: "DM Sans", padding: "8px 0",
            }}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} style={{ background: "none", border: "none", color: "#4a5568", cursor: "pointer", fontSize: 16 }}>✕</button>
          )}
        </div>

        <button className="add-btn" onClick={() => setShowModal(true)} style={{
          background: "linear-gradient(135deg, #1d4ed8, #0ea5e9)",
          border: "none", borderRadius: 10, padding: "9px 18px",
          color: "#fff", fontFamily: "Syne", fontWeight: 700, fontSize: 14,
          cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          boxShadow: "0 0 20px rgba(14,165,233,0.25)",
        }}>
          <span style={{ fontSize: 16 }}>+</span> Submit Tool
        </button>
      </header>

      <div style={{ display: "flex", flex: 1 }}>
        {/* SIDEBAR */}
        <aside style={{
          width: sidebarOpen ? 230 : 0,
          overflow: "hidden",
          transition: "width 0.28s cubic-bezier(.22,1,.36,1)",
          flexShrink: 0,
          borderRight: sidebarOpen ? "1px solid #151b30" : "none",
          background: "#080c18",
        }}>
          <div style={{ width: 230, padding: "18px 12px 24px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#3a4568", letterSpacing: "0.1em", textTransform: "uppercase", padding: "0 8px", marginBottom: 10 }}>
              Categories
            </div>
            {CATEGORIES.map(cat => (
              <button key={cat.id} className={`sidebar-link ${activeCategory === cat.id ? "active" : ""}`}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  background: "transparent", border: "none", borderRadius: 8, padding: "7px 10px",
                  color: activeCategory === cat.id ? "#63b3ed" : "#8892aa",
                  cursor: "pointer", fontSize: 13, fontFamily: "DM Sans", marginBottom: 1,
                  textAlign: "left",
                }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 15 }}>{cat.icon}</span>
                  <span style={{ fontWeight: activeCategory === cat.id ? 600 : 400 }}>{cat.label}</span>
                </span>
                <span style={{
                  fontSize: 11, background: "#111827", borderRadius: 5, padding: "1px 6px", color: "#4a5568"
                }}>{catCount(cat.id)}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* MAIN */}
        <main style={{ flex: 1, padding: "28px 32px", minWidth: 0 }}>
          {/* Page header */}
          <div style={{ marginBottom: 24, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
            <div>
              <h1 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 24, letterSpacing: "-0.5px" }}>
                {activeCat?.icon} {activeCat?.label}
              </h1>
              <p style={{ color: "#4a5568", fontSize: 13, marginTop: 4 }}>
                {searchQuery
                  ? `${filtered.length} result${filtered.length !== 1 ? "s" : ""} for "${searchQuery}"`
                  : `${filtered.length} tool${filtered.length !== 1 ? "s" : ""} listed`}
              </p>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              {SORT_OPTIONS.map(s => (
                <button key={s.id} className={`sort-btn ${sortBy === s.id ? "active" : ""}`}
                  onClick={() => setSortBy(s.id)}
                  style={{
                    background: sortBy === s.id ? "rgba(99,179,237,0.18)" : "transparent",
                    border: "1px solid #1e2540", borderRadius: 8, padding: "6px 14px",
                    color: sortBy === s.id ? "#63b3ed" : "#8892aa", fontFamily: "DM Sans",
                    fontSize: 13, cursor: "pointer", fontWeight: 500,
                  }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tool grid */}
          {loading ? (
            <div style={{ textAlign: "center", color: "#3a4568", padding: "80px 0", fontSize: 18 }}>Loading tools...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: "center", padding: "80px 0" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔭</div>
              <div style={{ color: "#4a5568", fontSize: 16 }}>No tools found</div>
              <div style={{ color: "#2a3050", fontSize: 13, marginTop: 6 }}>Be the first to submit one!</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {filtered.map((tool, idx) => (
                <ToolCard key={tool.id} tool={tool} idx={idx} onUpvote={handleUpvote} voted={votedIds.has(tool.id)} />
              ))}
            </div>
          )}
        </main>
      </div>

      {/* SUBMIT MODAL */}
      {showModal && (
        <div className="modal-bg" onClick={e => e.target === e.currentTarget && setShowModal(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          backdropFilter: "blur(4px)",
        }}>
          <div className="modal-box" ref={modalRef} style={{
            background: "#0d1020", border: "1px solid #1e2540", borderRadius: 18,
            width: "100%", maxWidth: 520, padding: "32px 28px",
            maxHeight: "90vh", overflowY: "auto",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
              <h2 style={{ fontFamily: "Syne", fontWeight: 800, fontSize: 20 }}>Submit an AI Tool ⚡</h2>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", color: "#4a5568", cursor: "pointer", fontSize: 22 }}>✕</button>
            </div>

            <FormField label="Tool Name *">
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. SuperWrite AI" {...inputStyle} />
            </FormField>

            <FormField label="URL *">
              <input value={form.url} onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
                placeholder="https://yourtool.com" {...inputStyle} />
            </FormField>

            <FormField label="Short Description *">
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What does this tool do? (max 180 chars)"
                maxLength={180} rows={3}
                style={{ ...inputStyle.style, resize: "vertical", lineHeight: 1.5 }} />
            </FormField>

            <FormField label="Category *">
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                style={{ ...inputStyle.style, cursor: "pointer" }}>
                {CATEGORIES.filter(c => c.id !== "all").map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Icon Emoji">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {["✨","🤖","🎨","💻","🚀","📚","🔬","🎬","🎵","📊","💼","🌍","⚙️","🛡️","🎮","📈","🏥","📱","🔍"].map(e => (
                  <span key={e} className={`emoji-opt ${form.emoji === e ? "sel" : ""}`}
                    onClick={() => setForm(f => ({ ...f, emoji: e }))}
                    style={{ fontSize: 22, cursor: "pointer" }}>{e}</span>
                ))}
              </div>
            </FormField>

            <FormField label="Your Name / Handle *">
              <input value={form.submittedBy} onChange={e => setForm(f => ({ ...f, submittedBy: e.target.value }))}
                placeholder="e.g. @techbuilder" {...inputStyle} />
            </FormField>

            {formError && (
              <div style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 8, padding: "10px 14px", color: "#f87171", fontSize: 13, marginBottom: 16 }}>
                ⚠️ {formError}
              </div>
            )}

            <button className="submit-btn" onClick={handleSubmit} disabled={submitting} style={{
              width: "100%", background: "linear-gradient(135deg, #1d4ed8, #0ea5e9)",
              border: "none", borderRadius: 12, padding: "13px", color: "#fff",
              fontFamily: "Syne", fontWeight: 700, fontSize: 15, cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.7 : 1, letterSpacing: "0.3px",
              boxShadow: "0 0 24px rgba(14,165,233,0.2)",
            }}>
              {submitting ? "Submitting..." : "🚀 Submit Tool"}
            </button>
          </div>
        </div>
      )}

      {/* NOTIFICATION */}
      {notification && (
        <div className="notif" style={{
          position: "fixed", bottom: 28, right: 28, zIndex: 300,
          background: notification.type === "warn" ? "#2d1f07" : "#071c14",
          border: `1px solid ${notification.type === "warn" ? "#b45309" : "#065f46"}`,
          borderRadius: 12, padding: "12px 20px",
          color: notification.type === "warn" ? "#fbbf24" : "#34d399",
          fontFamily: "DM Sans", fontSize: 14, fontWeight: 500,
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}>
          {notification.msg}
        </div>
      )}
    </div>
  );
}

const inputStyle = {
  style: {
    width: "100%", background: "#070b14", border: "1px solid #1e2540",
    borderRadius: 10, padding: "10px 14px", color: "#e8eaf0",
    fontFamily: "DM Sans", fontSize: 14, outline: "none",
  }
};

function FormField({ label, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#5a6880", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 7 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function ToolCard({ tool, idx, onUpvote, voted }) {
  const cat = CATEGORIES.find(c => c.id === tool.category);
  const [imgError, setImgError] = useState(false);
  const faviconUrl = getFaviconUrl(tool.url);

  return (
    <div className="tool-card" style={{
      background: "#0d1020", border: "1px solid #141828",
      borderRadius: 14, padding: "18px 20px",
      display: "flex", alignItems: "center", gap: 16,
    }}>
      {/* Rank */}
      <div style={{ color: "#252d40", fontFamily: "Syne", fontWeight: 800, fontSize: 13, minWidth: 22, textAlign: "center" }}>
        #{idx + 1}
      </div>

      {/* Logo */}
      <div style={{
        width: 50, height: 50, borderRadius: 13, flexShrink: 0,
        background: "linear-gradient(135deg, #111827, #1e2540)",
        display: "flex", alignItems: "center", justifyContent: "center",
        overflow: "hidden", fontSize: 24, border: "1px solid #1e2540",
      }}>
        {!imgError && faviconUrl ? (
          <img src={faviconUrl} alt="" width={32} height={32} onError={() => setImgError(true)} style={{ borderRadius: 6 }} />
        ) : (
          tool.emoji
        )}
      </div>

      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 4 }}>
          <a href={tool.url} target="_blank" rel="noopener noreferrer" className="tool-link" style={{
            fontFamily: "Syne", fontWeight: 700, fontSize: 16, color: "#e8eaf0",
            textDecoration: "none", letterSpacing: "-0.2px",
          }}>
            {tool.name}
          </a>
          <span style={{ color: "#2a3a5a", fontSize: 12 }}>↗</span>
          <span style={{
            background: "rgba(14,165,233,0.1)", border: "1px solid rgba(14,165,233,0.2)",
            borderRadius: 6, padding: "2px 8px", fontSize: 11, color: "#38bdf8", fontWeight: 600,
          }}>
            {cat?.icon} {cat?.label}
          </span>
        </div>
        <p style={{ color: "#6b7897", fontSize: 13, fontFamily: "DM Sans", lineHeight: 1.5, marginBottom: 6 }}>
          {tool.description}
        </p>
        <div style={{ display: "flex", alignItems: "center", gap: 14, fontSize: 11, color: "#3a4568" }}>
          <span>🔗 {getDomain(tool.url)}</span>
          <span>👤 {tool.submittedBy}</span>
          <span>🕐 {timeAgo(tool.createdAt)}</span>
        </div>
      </div>

      {/* Upvote */}
      <button className={`upvote-btn ${voted ? "voted" : ""}`} onClick={() => onUpvote(tool.id)} style={{
        background: voted ? "rgba(99,179,237,0.1)" : "transparent",
        border: `1px solid ${voted ? "#2a4a6a" : "#1e2540"}`,
        borderRadius: 10, padding: "8px 14px", cursor: "pointer",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 54,
        color: voted ? "#63b3ed" : "#6b7897",
      }}>
        <span style={{ fontSize: 18 }}>{voted ? "▲" : "△"}</span>
        <span style={{ fontFamily: "Syne", fontWeight: 700, fontSize: 14 }}>{tool.upvotes}</span>
      </button>
    </div>
  );
}